// What the two Storage pages share (HD-9): the Console page (pages/Storage.jsx, staff) and the Portal page
// (pages/storage/PortalStorage.jsx, external client accounts). Moved here unchanged from Storage.jsx, so both pages make
// the same calls with the same parameters. tests/acceptParity.test.mjs and tests/deletePermission.test.mjs read this file.
import { clean } from '../../shell/copy';

export const BASE = '/api/storage-archive';
export const PAGE_SIZE = 50;

// Mirrors CLIENT_ALLOWED_EXTS in the API's lib/storagePolicy.js. Only applied for EXTERNAL users: internal uploads
// are governed server-side by a denylist, so the picker is left unfiltered for them. Hand-mirrored copy of a server
// list; tests/acceptParity.test.mjs diffs the two and fails if they disagree.
export const ACCEPT_EXTS =
  '.mp4,.mov,.avi,.mkv,.m4v,.mxf,.r3d,.braw,.zip,'
  + '.wav,.aif,.aiff,.mp3,.aac,.m4a,.flac,.ogg,'
  + '.pdf,.doc,.docx,.txt,.rtf,.csv,.xlsx,.md,'
  + '.jpg,.jpeg,.png,.webp,.gif,.bmp,.heic,.heif,.avif,.svg,.tif,.tiff,.psd,.ai,.eps,.cube';
export const ACCEPT_RE =
  /\.(mp4|mov|avi|mkv|m4v|mxf|r3d|braw|wav|aif|aiff|mp3|aac|m4a|flac|ogg|zip|pdf|docx?|txt|rtf|csv|xlsx|md|jpe?g|png|webp|gif|bmp|heic|heif|avif|svg|tiff?|psd|ai|eps|cube)$/i;

export const UPLOAD_ENDPOINTS = {
  start:    `${BASE}/upload/multipart/start`,
  signPart: `${BASE}/upload/multipart/sign-part`,
  complete: `${BASE}/upload/multipart/complete`,
  abort:    `${BASE}/upload/multipart/abort`,
};

export const plural = (n, one, many = `${one}s`) => `${Number(n || 0).toLocaleString()} ${n === 1 ? one : many}`;
export function fmtDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
export const errMsg = (err, fallback) => clean(err?.response?.data?.error || err?.message) || fallback;

// Flatten the tree for a parent picker, excluding a moving folder's own subtree (the server refuses those too).
export function flattenTree(nodes, depth = 0, excludeId = null, out = []) {
  for (const n of nodes || []) {
    if (n.id === excludeId) continue;
    out.push({ id: n.id, name: n.name, depth });
    flattenTree(n.children, depth + 1, excludeId, out);
  }
  return out;
}
export const indentOptions = list => list.map(f => ({ value: f.id, label: `${' '.repeat(f.depth * 3)}${clean(f.name)}` }));

// Live has TWO clients named "TPK". Only collided names get the suffix, so every other row stays clean.
export function disambiguate(rows) {
  const seen = new Map();
  for (const r of rows) seen.set(r.clientName, (seen.get(r.clientName) || 0) + 1);
  return rows.map(r => ({ ...r, displayName: seen.get(r.clientName) > 1 ? `${clean(r.clientName)} (${String(r.clientId).slice(0, 4)})` : clean(r.clientName) }));
}

// Mirrors mayDeleteFile() in the API's routes/storageArchive.js: Admin, or the file's own uploader. External client
// users: own uploads only, never the admin branch. The server is the enforcement; this only decides whether to show it.
export function mayDeleteFile(perm, file) {
  if (!perm) return false;
  const isOwnUpload = !!file?.uploadedBy && file.uploadedBy === perm.userId;
  if (perm.isExternal) return isOwnUpload;
  return perm.isAdmin || isOwnUpload;
}

export function deleteBody(retention, many) {
  const it = many ? 'They disappear' : 'It disappears';
  const obj = many ? 'The files themselves are' : 'The file itself is';
  const days = retention?.graceDays ?? null;
  if (retention?.reaperArmed === true && days) {
    return `${it} from Storage straight away. ${obj} then permanently deleted from cold storage after ${days} days, no sooner`
      + `${many ? '' : ', and a little later for very recently uploaded files'}. Only an Admin can restore ${many ? 'them' : 'it'}, `
      + `and only inside that window. Once the ${days} days are up this cannot be undone.`;
  }
  return `${it} from Storage straight away. ${obj} kept in cold storage and can be restored by an Admin${many ? ' from Deleted files' : ''}.`;
}
